import _ from 'lodash';
import { accountSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/account';
import { AlertTriangle, PlusCircle } from '@lifeomic/chromicons';
import {
  authActions,
  authSelectors,
} from '@lifeomic/phc-web-toolkit/src/redux/modules/auth';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { Header as ChromaHeader } from '@lifeomic/chroma-react/components/Header';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import { MaskedModeSwitchContainer as MaskedModeSwitch } from '@lifeomic/phc-web-toolkit/src/components/MaskedModeSwitch';
import { DarkModeSwitchContainer as DarkModeSwitch } from '@lifeomic/phc-web-toolkit/src/components/DarkModeSwitch';
import { FLSymbolReverse } from '@lifeomic/phc-web-toolkit/src/components/Logos';
import ProjectPicker from '../ProjectPicker';
import { projectPickerActions } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker';
import { push } from 'connected-react-router';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { userSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/user';
import { withRouter, Link } from 'react-router-dom';
import { withStyles } from '@lifeomic/chroma-react/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { ShareLinkButton } from '@lifeomic/phc-web-toolkit/src/components/ShareLinkButton';
import urls from '../../../util/urls';
import UserMenu from './UserMenu';
import { TrimmedLogo } from './TrimmedLogo';
import { getTestProps } from '../../../util/testProps';
import { featuresSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/features';
import ProjectCohortPicker from '../ProjectCohortPicker';
import EnforceAccountFeatureToggle from '@lifeomic/phc-web-toolkit/src/components/EnforceAccountFeatureToggle';

const styles = theme => ({
  header: {
    color: theme.palette.common.white,
    paddingLeft: theme.spacing(1),
  },
  warningIcon: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(2),
  },
  buttonLink: {
    '&:hover': {
      textDecoration: 'none',
    },
  },
});

const messages = defineMessages({
  accountLogo: {
    id: 'header.accountLogo',
    defaultMessage: 'Account logo',
  },
});

class Header extends React.PureComponent {
  toggleBarsVisibility = () => {
    const root = document.documentElement;
    const isHidden = root.dataset.appBarsHidden === 'true';

    if (isHidden) {
      root.dataset.appBarsHidden = 'false';
      root.style.removeProperty('--app-sidebar-width');
      root.style.removeProperty('--app-sidebar-collapsed-width');
      root.style.removeProperty('--app-header-height');
      root.style.removeProperty('--app-header-min-height');
      root.style.removeProperty('--app-header-max-height');
      root.style.removeProperty('--app-header-overflow');
      return;
    }

    root.dataset.appBarsHidden = 'true';
    root.style.setProperty('--app-sidebar-width', '0px');
    root.style.setProperty('--app-sidebar-collapsed-width', '0px');
    root.style.setProperty('--app-header-height', '0px');
    root.style.setProperty('--app-header-min-height', '0px');
    root.style.setProperty('--app-header-max-height', '0px');
    root.style.setProperty('--app-header-overflow', 'hidden');
  };

  componentDidMount() {
    if (this.props.auth.isAuthenticated && this.props.activeAccount)
      this.props.fetchProjects({ params: { pageSize: 1000 } });
  }

  componentDidUpdate(nextProps) {
    if (
      nextProps.activeAccount &&
      nextProps.activeAccount.id !== (this.props.activeAccount || {}).id
    ) {
      this.props.fetchProjects({ params: { pageSize: 1000 } });
    }
  }

  handleChange = (e, value) => this.props.push(value);

  handleAccountClick = () => {
    const { onAccountClick } = this.props;

    if (typeof onAccountClick !== 'function') return;

    onAccountClick();
  };

  render() {
    const {
      accounts,
      activeAccount,
      auth,
      classes,
      features,
      intl,
      logout,
      globalLogout,
      push,
      user,
    } = this.props;

    const userDeletionDate = _.get(user, 'response.profile.userDeletionDate');
    const { formatMessage } = intl;

    return (
      <ChromaHeader
        className={classes.header}
        centerLogo={true}
        onDoubleClick={this.toggleBarsVisibility}
        logo={
          <Link
            to={urls.app.home()}
            id="header-breadcrumbs-home"
            {...getTestProps('header-home-link')}
          >
            {activeAccount && activeAccount.logo ? (
              <TrimmedLogo
                alt={formatMessage(messages.accountLogo)}
                logoUrl={activeAccount.logo}
              />
            ) : (
              <FLSymbolReverse />
            )}
          </Link>
        }
        left={
          accounts.length > 0 ? (
            <>
              <Button
                color="inverse"
                variant="text"
                onClick={this.handleAccountClick}
                className={classes.accountButton}
                {...getTestProps('account-switcher-button')}
              >
                {activeAccount && activeAccount.name}
              </Button>
              {'/'}
              <ProjectPicker showLoader={false} />
              <EnforceAccountFeatureToggle accountFeature="cohorts">
                <>
                  {'/'}
                  <ProjectCohortPicker showLoader={false} />
                </>
              </EnforceAccountFeatureToggle>
            </>
          ) : (
            <Link to={urls.app.createAccount()} className={classes.buttonLink}>
              <Button
                variant="text"
                icon={PlusCircle}
                className={classes.accountButton}
              >
                <FormattedMessage
                  id="header.createAnAccount"
                  defaultMessage="Create Account"
                />
              </Button>
            </Link>
          )
        }
        right={
          auth.isAuthenticated && (
            <>
              <ShareLinkButton key="share-link-1" />
              <DarkModeSwitch key="dark-mode-switch" />
              <MaskedModeSwitch key="masked-mode-switch" />
              <UserMenu
                key="user-menu"
                {...{
                  user,
                  logout,
                  globalLogout,
                  push,
                }}
              />
              {userDeletionDate && (
                <Tooltip
                  key="warning"
                  title={formatMessage({
                    id: 'header.warningIcon.tooltip',
                    defaultMessage:
                      'Your account is going to be deleted.  Edit your profile to cancel the deletion.',
                  })}
                >
                  <span>
                    <AlertTriangle className={classes.warningIcon} />
                  </span>
                </Tooltip>
              )}
            </>
          )
        }
      />
    );
  }
}

Header.propTypes = {
  children: PropTypes.any,
  auth: PropTypes.object.isRequired,
  classes: PropTypes.any,
  logout: PropTypes.func,
  globalLogout: PropTypes.func,
  user: PropTypes.object,
  push: PropTypes.func.isRequired,
  features: PropTypes.object,
  location: PropTypes.object,
  onAccountClick: PropTypes.func,
};

const mapStateToProps = state => ({
  user: userSelectors.selectUserState(state),
  auth: authSelectors.selectAuthState(state),
  location: state.router.location,
  activeAccount: accountSelectors.selectActiveAccount(state),
  accounts: accountSelectors.selectAccounts(state),
  features: featuresSelectors.selectFeatures(state),
});

const mapDispatchToProps = {
  push,
  hydrateActiveProject: projectPickerActions.hydrateActiveProject,
  fetchProjects: projectPickerActions.fetchProjects,
  logout: authActions.logout,
  globalLogout: authActions.globalLogout,
};

export default compose(
  injectIntl,
  withStyles(styles, { withTheme: true }),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(Header);
